import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LockCircle } from './LockCircle';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { useGatingStore, type QuotaUpsellDetails } from '@/stores/gating.store';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useTrack } from '@/hooks/useTrack';
import type { ShopEntry } from '@/utils/shopTracking';
import {
  getFeatureCopy,
  getPlanDisplayName,
  getPlatformLabel,
  getQuotaCardCopy,
  getShopPlanPath,
} from './gatingCopy';

interface LockedFeatureCardProps {
  /** Entitlement feature key, e.g. 'analytics_hub' (for the quota / platform variants: the dedup key) */
  feature: string;
  /** 'feature' (default): plan teaser. 'quota': monthly limit reached. 'platform': platform not in the plan. */
  variant?: 'feature' | 'quota' | 'platform';
  /** Required for variant 'quota' */
  quota?: QuotaUpsellDetails;
  /** Required for variant 'platform' (enum value, e.g. 'LINKEDIN') */
  platform?: string;
  /** Cheapest plan with the feature; falls back to what the gating store learned, then to the generic wording */
  upsellPlanSlug?: string | null;
  /** Smaller padding / no benefit line spacing, for use inside a dashboard section or a modal */
  compact?: boolean;
  /** Called before navigating (e.g. to close the modal the card sits in) */
  onNavigate?: () => void;
  /** Shop entry reported when the CTA navigates (the global upsell modal passes 'upsell'). Default 'locked_card'. */
  entry?: ShopEntry;
  className?: string;
}

/**
 * Calm "available on {Plan}" teaser shown in place of a gated feature.
 * Lock icon, plan name, one value line, CTA to the plan in the Shop.
 */
export const LockedFeatureCard = ({
  feature,
  variant = 'feature',
  quota,
  platform,
  upsellPlanSlug,
  compact = false,
  onNavigate,
  entry = 'locked_card',
  className,
}: LockedFeatureCardProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const learnedSlug = useGatingStore((s) => s.lockedSlugs[feature] ?? null);

  const slug = upsellPlanSlug ?? learnedSlug;
  const planName = getPlanDisplayName(slug);
  const track = useTrack();
  const gateProps = {
    key: feature,
    kind: variant,
    ...(slug ? { targetPlanSlug: slug } : {}),
    from: variant === 'feature' ? 'locked_card' : variant,
  };
  const gateKey = `${feature}|${variant}|${slug ?? ''}`;

  // Once per rendered gate (the card is also what GatingHost shows for quota / platform walls).
  useEffect(() => {
    track('gate_shown', gateProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateKey]);
  const { formatDateOnly } = useTimezone();

  let copy = getFeatureCopy(t, feature);
  if (variant === 'quota' && quota) {
    copy = getQuotaCardCopy(t, quota, formatDateOnly);
  } else if (variant === 'platform' && platform) {
    const label = getPlatformLabel(platform);
    copy = {
      title: t('gating_platform_title', { platform: label }),
      benefit: t('gating_platform_benefit', { platform: label }),
    };
  }

  return (
    <section
      aria-label={copy.title}
      data-testid="locked-feature-card"
      data-feature={feature}
      data-variant={variant}
      className={cn(
        'flex flex-col items-center text-center rounded-xl border border-primary-200 bg-primary-50/40',
        compact ? 'px-4 py-5' : 'px-6 py-10',
        className
      )}
    >
      <LockCircle
        size={compact ? 40 : 56}
        glyphSize={compact ? 18 : 24}
        className={cn('bg-primary-100 text-primary-600', compact ? 'mb-3' : 'mb-4')}
      />
      <h3 className="text-base font-semibold text-gray-900">{copy.title}</h3>
      <p className="mt-1 text-sm font-medium text-primary-700">
        {variant === 'quota'
          ? planName
            ? t('gating_quota_higher_on', { plan: planName })
            : t('gating_quota_higher_generic')
          : planName
            ? t('gating_available_on', { plan: planName })
            : t('gating_available_on_generic')}
      </p>
      <p className={cn('text-sm text-gray-600 max-w-md', compact ? 'mt-1.5' : 'mt-2')}>{copy.benefit}</p>
      <Button
        variant="primary"
        size="sm"
        className={compact ? 'mt-3' : 'mt-5'}
        onClick={() => {
          track('upsell_clicked', gateProps);
          onNavigate?.();
          navigate(getShopPlanPath(slug), { state: { entry } });
        }}
      >
        {planName ? t('gating_cta_view_plan', { plan: planName }) : t('gating_cta_view_plans')}
      </Button>
    </section>
  );
};

export default LockedFeatureCard;
